/**
 * Purpose: Renders the content of a message bubble
 *
 * How it works:
 * 1. Renders different message content types (text, attachments, links)
 * 2. Applies appropriate styling based on message properties
 * 3. Handles emoji-only messages with special formatting
 *
 * Key features:
 * - Parent message reference display
 * - Media and file attachment rendering
 * - Text content with emoji detection
 * - Link preview rendering
 * - Timestamp display
 */

import { cn, isValidLink } from "@/lib/utils";
import { MessageEntity } from "@/indexedDB/types";
import { useUploadProgressState } from "@/zustand/UploadProgressState";
import ParentMessage from "./ParentMessage";
import MessageAttachment from "./attachments/MessageAttachment";
import MessageText from "./MessageText";
import MessageTimestamp from "./MessageTimestamp";
import MessageLink from "./MessageLink";

interface Props {
  message: MessageEntity;
}

export default function MessageBubbleContent({ message }: Props) {
  const attachmentId = message.content.media?.attachmentId;
  const isUploading = useUploadProgressState(
    (state) => attachmentId !== undefined && attachmentId in state.progress,
  );

  return (
    <>
      {/* PARENT MESSAGE */}
      {message.parentMessage && (
        <ParentMessage
          id={message.parentMessage.id}
          chatId={message.chatId}
          className={"border-l-4 border-secondary/40 bg-card/60"}
        />
      )}

      {/* MESSAGE ATTACHMENT */}
      {message.hasAttachment && (
        <div
          className={cn(
            // min-w-0: this is a grid item; without it the item's min-content
            // width pins the media bubble to its definite width and it can't
            // shrink to the column on mobile.
            "relative w-full min-w-0 select-none",
            message.isOutgoing ? "justify-self-end" : "justify-self-start",
          )}
        >
          <MessageAttachment
            messageId={message.id}
            messageTimestamp={message.timestamp}
            chatId={message.chatId}
            messageContent={message.content}
            isOutgoingMessage={message.isOutgoing}
            threadId={message.threadId}
          />

          {!message.content.text.length &&
            !message.content.file &&
            !isUploading && (
              <MessageTimestamp
                className={cn(
                  "absolute bottom-2 right-2 select-none",
                  message.content.voice && "bottom-0 right-0",
                )}
                timestamp={message.timestamp}
                isOutgoing={message.isOutgoing}
                isPinned={message.isPinned}
                sendingStatus={message.sendingStatus}
                deliveryUnconfirmed={message.deliveryUnconfirmed}
              />
            )}
        </div>
      )}

      {/* MESSAGE LINK  */}
      {isValidLink(message.content.link) && !message.hasAttachment && (
        <MessageLink message={message} />
      )}

      {/* MESSAGE TEXT  */}
      {message.content.text && (
        <MessageText
          text={message.content.text}
          isOutgoingMessage={message.isOutgoing}
          isPinned={message.isPinned}
          hasMediaAttachment={!!message.content.media}
          sendingStatus={message.sendingStatus}
          deliveryUnconfirmed={message.deliveryUnconfirmed}
          timestamp={message.timestamp}
          tags={message.tags}
          // Files put the timestamp on its own line below (see the block after
          // this) rather than inline, so a short caption isn't stranded next to
          // the time in the file card's wide bubble.
          withTimestamp={!message.content.file}
        />
      )}

      {/* MESSAGE TIMESTAMP — file messages (with or without a caption) show the
          timestamp here, on its own line below, right-aligned by MessageTimestamp. */}
      {message.content.file && (
        <MessageTimestamp
          className="select-none"
          timestamp={message.timestamp}
          isOutgoing={message.isOutgoing}
          isPinned={message.isPinned}
          sendingStatus={message.sendingStatus}
          deliveryUnconfirmed={message.deliveryUnconfirmed}
        />
      )}
    </>
  );
}
